import { useState, useCallback } from 'react';
import { useSpringPageQuery } from '../../../shared/hooks/use-spring-page-query';
import { notificationsApi, type InboxNotificationDto } from '../api/notifications.api';
import { useAuthProviderV2 } from '../../../providers/provider-hooks';

/**
 * Hook for managing received notifications (inbox)
 *
 * Citeste GET /api/notifications/inbox - ruta scopata SERVER-side pe utilizatorul din token.
 *
 * ⚠️ Pana pe 2026-09-01 chema /report cu filtrul `receiver.id`. PR-ul de securitate din 2026-08-28
 * a inchis /report la DBEST+ADMIN (tocmai pentru ca filtrul pe destinatar venea din cerere), deci
 * inbox-ul PROFESOR/ELEV lua 403 - si, pentru ca SDK-ul nu arunca la eroare, UI-ul afisa linistit
 * „0 rezultate". Nu repointa inapoi pe /report. → defectul D3 / T7-S12.
 *
 * Sortarea nu se trimite: ordinea (necitite intai, apoi descrescator dupa sendAt) e fixata in
 * interogarea de pe server, iar un `sort=` trimis de client ar produce 500, nu o alta ordine.
 *
 * @example
 * const { notifications, readNotification, loading } = useReceivedNotifications();
 */
export function useReceivedNotifications() {
    const { userInfo } = useAuthProviderV2();
    const [openedNotification, setOpenedNotification] = useState<string>('');

    const report = useSpringPageQuery<InboxNotificationDto, Record<string, never>>({
        queryKey: ['notifications', 'inbox', userInfo?.userId || ''],
        fetchFn: (_filter, pageable) => notificationsApi.getInbox(pageable),
        initialFilter: {},
        initialPageable: {
            page: 0,
            size: 20,
            sort: []
        },
        queryOptions: {
            refetchOnMount: 'always', // Always fetch fresh data when tab becomes active
            staleTime: 0, // Consider data immediately stale (always refetch)
        }
    });

    /**
     * Read notification - marks as read and refetches list
     */
    const readNotification = useCallback(async (notificationId: string) => {
        const fullNotification = report.data.find((n) => n.id === notificationId);

        if (!fullNotification?.content) {
            console.warn('No content found for notification:', notificationId);
            return;
        }

        // Mark as read if not already read
        if (!fullNotification.isRead) {
            try {
                await notificationsApi.markAsRead(notificationId);
                // Refetch to update read status
                await report.refetch();
            } catch (error) {
                console.error('Failed to mark notification as read:', error);
            }
        }

        // Show notification content
        setOpenedNotification(fullNotification.content);
    }, [report.data, report.refetch]);

    /**
     * Count unread notifications
     */
    const unreadCount = report.data.filter(n => !n.isRead).length;

    return {
        // Data
        notifications: report.data,
        unreadCount,

        // Loading states
        loading: report.loading,
        isLoading: report.isLoading,

        // Starea de eroare. Se expune pentru ca pagina TREBUIE sa aiba ce randa cand cererea
        // pica: la defectul D3 ruta intorcea 403, `handleResponse` arunca corect, React Query
        // retinea eroarea - dar hook-ul n-o dadea mai departe, deci UI-ul afisa senin
        // „1 - 0 din 0 rezultate". Un inbox gol si un inbox interzis aratau identic.
        isError: report.isError,
        error: report.error,

        // Pagination
        pagination: {
            currentPage: report.currentPage,
            totalPages: report.totalPages,
            totalElements: report.totalElements,
            pageSize: report.pageSize,
            first: report.currentPage === 0,
            last: report.currentPage === report.totalPages - 1,
        },
        goToPage: report.goToPage,
        changePageSize: report.changePageSize,

        // Actions
        readNotification,
        refetch: report.refetch,

        // Dialog state
        openedNotification,
        setOpenedNotification,
    };
}
